import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["questionKey"];
import ComparePlansLink from 'pricing-pages-lib/components/ComparePlansLink';
import { LIMIT_USAGE } from 'pricing-pages-lib/constants/EarlyRequesterNames';
import ProductInfoContainer from 'pricing-pages-lib/js/components/ProductInfoContainer.tsx';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { CMS, MARKETING, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { SUITE_STARTER } from 'self-service-api/constants/UpgradeProducts';
import { buildFilteredHighlightedProducts } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import { getPrimaryTracker } from 'usage-tracker-container';
import WhyGoHubSpotSection from '../components/WhyGoHubSpotSection';
import BannerSection from '../components/banners/BannerSection';
import PhoneSupport from '../components/common/PhoneSupport';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import CodeSplitEnvWrapper from '../components/common/env/CodeSplitEnvWrapper';
import FailedLoadBanner from '../components/common/loading/FailedLoadBanner';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import Faq from '../components/faqs/Faq';
import FooterMicrocopy from '../components/faqs/FooterMicrocopy';
import Question from '../components/faqs/Question';
import SuiteFeatureTable from '../components/feature-table/suite-feature-table/SuiteFeatureTable';
import HeaderLayout from '../components/layout/HeaderLayout';
import { SuiteTiles } from '../components/merch-card/SuiteTiles';
import { FaqsConfig } from '../constants/faqs/FaqsConfig';
import { getMerchCardsToShow } from '../constants/merchCards/MerchCardsToShow';
import { NavItemEnum } from '../constants/routing/NavItems';
import { SCREENS } from '../constants/tracking/Screens';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useTrackRetail } from '../hooks/useTrackRetail';
import { useAppSelector } from '../reducers';
import { hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { productsFromProductInventorySelector } from '../selectors/ProductInventorySelectors';
import { trackPageView } from '../utils/tracking/trackingEventUtils';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const FreeToolsPage = () => {
  useDocumentTitle('crm');
  useTrackRetail();
  const isAppReady = useGlobalLoading();
  const hasPricingAndPurchasingFailed = useAppSelector(hasPricingAndPurchasingFailedSelector);
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const ownedProducts = useSelector(state => {
    if (!isAppReady) return [];
    return productsFromProductInventorySelector(state);
  });
  const isPaidPortal = ownedProducts && ownedProducts.length > 0;
  useEffect(() => {
    getUsageTracker().setProperties({
      defaultedTab: '',
      tab: ''
    });
    trackPageView({
      screen: SCREENS.FREE
    });
  }, []);
  const productLevels = getMerchCardsToShow({
    isFreeToStarter: true
  });
  const marketingHighlightedProducts = buildFilteredHighlightedProducts({
    productLine: MARKETING,
    productLevels
  });
  const salesHighlightedProducts = buildFilteredHighlightedProducts({
    productLevels,
    productLine: SALES
  });
  const serviceHighlightedProducts = buildFilteredHighlightedProducts({
    productLevels,
    productLine: SERVICE
  });
  const operationsHighlightedProducts = buildFilteredHighlightedProducts({
    productLevels,
    productLine: OPERATIONS
  });
  const cmsHighlightedProducts = buildFilteredHighlightedProducts({
    productLevels,
    productLine: CMS
  });
  const productsByProductLine = {
    [MARKETING]: marketingHighlightedProducts,
    [SALES]: salesHighlightedProducts,
    [SERVICE]: serviceHighlightedProducts,
    [OPERATIONS]: operationsHighlightedProducts,
    [CMS]: cmsHighlightedProducts
  };
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs("div", {
      "data-selenium-id": "page-success",
      children: [isAppReady && /*#__PURE__*/_jsx(NavMarker, {
        name: "FREE_TOOLS_PAGE"
      }), /*#__PURE__*/_jsx(HeaderLayout, {
        selectedNavItem: NavItemEnum.FREE,
        children: /*#__PURE__*/_jsxs(ProductInfoContainer, {
          children: [/*#__PURE__*/_jsx(BannerSection, {
            screen: NavItemEnum.FREE
          }), /*#__PURE__*/_jsx(FailedLoadBanner, {
            hasFailed: hasPricingAndPurchasingFailed
          }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
            children: [/*#__PURE__*/_jsx(ProductPageTitle, {
              nonProductIdentifier: NavItemEnum.FREE
            }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
              nonProductIdentifier: NavItemEnum.FREE
            })]
          }), /*#__PURE__*/_jsx(SuiteTiles, {
            isFreeToStarter: true,
            screen: SCREENS.FREE
          }), /*#__PURE__*/_jsx(ComparePlansLink, {}), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
            authedComponentLoader: () => import( /* webpackChunkName: "authed-chunk" */`pricing-pages-ui-components/limit-usage/LimitUsageSection`),
            authedComponentProps: {
              isFreePortal: !isPaidPortal,
              earlyRequestName: LIMIT_USAGE,
              usageTracker: getPrimaryTracker()
            }
          }), /*#__PURE__*/_jsx(PageSection, {
            children: /*#__PURE__*/_jsx(SuiteFeatureTable, {
              productsByProductLine: productsByProductLine,
              screen: SCREENS.FREE,
              isFreeToStarter: true
            })
          }), /*#__PURE__*/_jsx(WhyGoHubSpotSection, {
            selectedNavItem: NavItemEnum.FREE
          }), /*#__PURE__*/_jsx(PageSection, {
            children: /*#__PURE__*/_jsx(PhoneSupport, {
              phoneSourceId: "crm-middle",
              screen: SCREENS.FREE,
              upgradeProduct: SUITE_STARTER
            })
          }), /*#__PURE__*/_jsxs(Faq, {
            children: [FaqsConfig[NavItemEnum.FREE].map(_ref => {
              let {
                  questionKey
                } = _ref,
                passthroughOptions = _objectWithoutPropertiesLoose(_ref, _excluded);
              return /*#__PURE__*/_jsx(Question, Object.assign({
                questionKey: questionKey
              }, passthroughOptions), questionKey);
            }), /*#__PURE__*/_jsx(FooterMicrocopy, {})]
          })]
        })
      })]
    })
  });
};
export default FreeToolsPage;